import { useNavigate } from 'react-router-dom'
import { ShieldCheck, Users, ClipboardCheck, BarChart3, ArrowRight, Activity, Sparkles, Lock, Zap } from 'lucide-react'

export default function LandingPage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen w-full flex bg-[#FAF6EE]">
      {/* Left panel — dark branding */}
      <div className="relative hidden lg:flex lg:w-[45%] xl:w-[42%] flex-col justify-between overflow-hidden bg-[#0F172A] text-white p-12 xl:p-14">
        {/* Animated gradient orbs */}
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#F0821F]/30 blur-3xl animate-pulse" />
        <div className="absolute top-1/3 -right-32 w-96 h-96 rounded-full bg-[#A31621]/30 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute -bottom-32 left-1/4 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />

        {/* Top bar */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-lg shadow-black/20">
            <img src="/logo.png" alt="IRCS Haryana" className="w-9 h-9 object-contain" />
          </div>
          <div>
            <p className="font-bold leading-tight text-lg">HRMS</p>
            <p className="text-xs text-white/60 leading-tight">Human Resource Management</p>
          </div>
        </div>

        {/* Center content */}
        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F0821F]/20 to-[#E4670F]/20 border border-[#F0821F]/30 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide text-[#FBBF6E] mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F0821F] animate-pulse" />
            INDIAN RED CROSS SOCIETY · HARYANA
          </div>

          <h1
            className="text-5xl xl:text-6xl font-bold leading-[1.05] mb-6 tracking-tight"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Internal
            <br />
            <span className="bg-gradient-to-r from-[#F0821F] via-[#E4670F] to-[#A31621] bg-clip-text text-transparent">
              Systems Portal
            </span>
          </h1>

          <p className="text-white/70 text-base leading-relaxed mb-10 max-w-sm">
            Choose your workspace and sign in to access the tools you need — all in one unified platform.
          </p>

          {/* Feature bullets */}
          <div className="space-y-3">
            {[
              { icon: Zap, label: 'Real-time attendance & reporting' },
              { icon: Lock, label: 'Role-based secure access' },
              { icon: Activity, label: 'Live executive dashboards' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3 text-white/70 text-sm">
                <div className="w-8 h-8 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#F0821F]" />
                </div>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 flex items-center gap-3 text-white/40 text-xs">
          <div className="flex-1 h-px bg-white/10" />
          <span>© {new Date().getFullYear()} IRCS Haryana</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
      </div>

      {/* Right panel — portal selection */}
      <div className="flex-1 flex flex-col relative">
        {/* Mobile top brand */}
        <div className="lg:hidden flex items-center justify-center gap-3 pt-8 pb-6">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-md ring-1 ring-neutral-200">
            <img src="/logo.png" alt="IRCS Haryana" className="w-9 h-9 object-contain" />
          </div>
          <div>
            <p className="font-bold text-neutral-900 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
              HRMS
            </p>
            <p className="text-xs text-neutral-500 leading-tight">Human Resource Management System</p>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 py-8">
          <div className="w-full max-w-xl">
            {/* Heading */}
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase mb-4">
                <Sparkles className="w-3 h-3" />
                Select your workspace
              </span>
              <h2
                className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-3"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Welcome back
              </h2>
              <p className="text-neutral-500 text-sm">
                Choose the portal you'd like to sign in to.
              </p>
            </div>

            {/* Portal cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Employee Portal */}
              <button
                onClick={() => navigate('/login')}
                className="group relative bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-6 flex flex-col items-start gap-4 text-left transition-all duration-300 hover:shadow-xl hover:shadow-[#E4670F]/10 hover:-translate-y-1 hover:ring-[#E4670F]/40 overflow-hidden"
              >
                {/* Hover glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#F0821F]/[0.04] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="relative flex items-center justify-between w-full">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#C24A0A] flex items-center justify-center shadow-md shadow-[#E4670F]/30 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-neutral-300 group-hover:text-[#C24A0A] group-hover:translate-x-1 transition-all duration-300" />
                </div>

                <div className="relative">
                  <p className="text-lg font-bold text-neutral-900 mb-1">Employee Portal</p>
                  <p className="text-sm text-neutral-500 leading-relaxed">
                    Attendance, EOD reports, leave requests &amp; holidays.
                  </p>
                </div>

                <div className="relative flex items-center gap-2 text-xs text-neutral-400 mt-auto pt-2">
                  <ClipboardCheck className="w-3.5 h-3.5" />
                  <span>Daily operations workspace</span>
                </div>
              </button>

              {/* Executive Portal */}
              <button
                onClick={() => navigate('/executive/login')}
                className="group relative bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-6 flex flex-col items-start gap-4 text-left transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10 hover:-translate-y-1 hover:ring-blue-400/50 overflow-hidden"
              >
                {/* Hover glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/[0.04] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="relative flex items-center justify-between w-full">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-900 flex items-center justify-center shadow-md shadow-blue-900/30 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-neutral-300 group-hover:text-blue-700 group-hover:translate-x-1 transition-all duration-300" />
                </div>

                <div className="relative">
                  <p className="text-lg font-bold text-neutral-900 mb-1">Executive Portal</p>
                  <p className="text-sm text-neutral-500 leading-relaxed">
                    Live, read-only monitoring for enrollment &amp; revenue insights.
                  </p>
                </div>

                <div className="relative flex items-center gap-2 text-xs text-neutral-400 mt-auto pt-2">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  <span>Restricted · Read-only access</span>
                </div>
              </button>
            </div>

            {/* Footer note */}
            <div className="flex items-center justify-center gap-2 mt-10 text-neutral-400 text-xs">
              <Activity className="w-3.5 h-3.5" />
              <span>Secured by HRMS · Authorized personnel only</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}



// import { useNavigate } from 'react-router-dom'
// import { ShieldCheck, Users, ClipboardCheck, BarChart3, ArrowRight } from 'lucide-react'

// export default function LandingPage() {
//   const navigate = useNavigate()

//   return (
//     <div className="h-screen w-full overflow-hidden flex flex-col bg-[#FAF6EE]">
//       {/* Header */}
//       <div className="flex items-center justify-center gap-3 pt-8 pb-6 sm:pt-10 sm:pb-8">
//         <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md flex-shrink-0">
//           <img src="/logo.png" alt="IRCS Haryana" className="w-9 h-9 object-contain" />
//         </div>
//         <div>
//           <p className="font-bold text-neutral-900 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
//             Indian Red Cross Society
//           </p>
//           <p className="text-sm text-neutral-500 leading-tight">Haryana State Branch</p>
//         </div>
//       </div>

//       {/* Main */}
//       <div className="flex-1 flex flex-col items-center justify-center px-4 pb-8 overflow-y-auto">
//         <div className="text-center mb-10 max-w-lg">
//           <span className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase mb-4">
//             Serving Humanity Since 1966
//           </span>
//           <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-3" style={{ fontFamily: 'Georgia, serif' }}>
//             Internal Systems Portal
//           </h1>
//           <p className="text-neutral-500">Select the workspace you'd like to sign in to.</p>
//         </div>

//         <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full max-w-3xl">
//           {/* Staff Portal card */}
//           <button
//             onClick={() => navigate('/login')}
//             className="group relative bg-white rounded-2xl shadow-sm border border-neutral-200 p-8 flex flex-col items-start gap-4 text-left hover:shadow-lg hover:border-[#E4670F]/40 transition-all"
//           >
//             <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#C24A0A] flex items-center justify-center shadow-sm">
//               <Users className="w-6 h-6 text-white" />
//             </div>
//             <div>
//               <p className="text-lg font-bold text-neutral-900">Staff Portal</p>
//               <p className="text-sm text-neutral-500 mt-1">
//                 Attendance, EOD reports, leave requests &amp; certificates for staff members.
//               </p>
//             </div>
//             <div className="flex items-center gap-2 mt-2 text-xs text-neutral-400">
//               <ClipboardCheck className="w-3.5 h-3.5" />
//               <span>Daily operations workspace</span>
//             </div>
//             <span className="absolute top-6 right-6 inline-flex items-center gap-1 text-sm font-semibold text-[#C24A0A] opacity-0 group-hover:opacity-100 transition-opacity">
//               Continue <ArrowRight className="w-4 h-4" />
//             </span>
//           </button>

//           {/* Executive Portal card */}
//           <button
//             onClick={() => navigate('/executive/login')}
//             className="group relative bg-white rounded-2xl shadow-sm border border-neutral-200 p-8 flex flex-col items-start gap-4 text-left hover:shadow-lg hover:border-blue-400/50 transition-all"
//           >
//             <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-900 flex items-center justify-center shadow-sm">
//               <BarChart3 className="w-6 h-6 text-white" />
//             </div>
//             <div>
//               <p className="text-lg font-bold text-neutral-900">Executive Portal</p>
//               <p className="text-sm text-neutral-500 mt-1">
//                 Live, read-only monitoring dashboard for enrollment and revenue insights.
//               </p>
//             </div>
//             <div className="flex items-center gap-2 mt-2 text-xs text-neutral-400">
//               <ShieldCheck className="w-3.5 h-3.5" />
//               <span>Restricted access · Read-only</span>
//             </div>
//             <span className="absolute top-6 right-6 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity">
//               Continue <ArrowRight className="w-4 h-4" />
//             </span>
//           </button>
//         </div>

//         <p className="text-center text-neutral-400 text-xs mt-10">
//           Indian Red Cross Society — Haryana State Branch
//         </p>
//       </div>
//     </div>
//   )
// }